import { inject, Injector } from '@angular/core';
import { MatDialogConfig } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { BaseComponent } from '../base/base.component';
import { openConsentDialog, openSimpleDialog, SimpleDialogData } from './dialog.component';

/** Constructor shape accepted by the mixin — any (abstract) subclass of `BaseComponent`. */
type AbstractConstructor<T> = abstract new (...args: any[]) => T;

/**
 * The public surface the mixin adds. Declaring it explicitly (and using it as the factory's
 * return type) is what keeps TypeScript from trying to emit the anonymous mixin class — the cause
 * of TS4094 ("... may not be private or protected") for the injected/inherited private fields.
 */
export interface DialogCapableComponent {
  /**
   * Opens an `immo-dialog` with the given slots, scoped to this component's lifetime.
   *
   * @typeParam TReturn - The type the dialog resolves with when closed.
   * @typeParam TContext - The type of the data handed to the rendered content.
   * @param data - The dialog slots (header / content / footer) and context.
   * @param config - Optional `MatDialogConfig` overrides.
   * @returns An observable emitting the dialog result, or `undefined` if dismissed.
   */
  openDialog<TReturn = unknown, TContext = unknown>(
    data: Partial<SimpleDialogData<TReturn, TContext>>,
    config?: Omit<MatDialogConfig<SimpleDialogData<TReturn, TContext>>, 'data'>,
  ): Observable<TReturn | undefined>;

  /**
   * Opens a yes/no consent dialog, resolving to a strict boolean.
   *
   * @typeParam TContext - The type of the data handed to the rendered content.
   * @param data - The dialog slots and context.
   * @param config - Optional `MatDialogConfig` overrides.
   * @returns An observable emitting `true` only when closed with `true`, otherwise `false`.
   */
  openConsentDialog<TContext = unknown>(
    data: Partial<SimpleDialogData<boolean, TContext>>,
    config?: Omit<MatDialogConfig<SimpleDialogData<boolean, TContext>>, 'data'>,
  ): Observable<boolean>;
}

/**
 * Mixin that adds `openDialog` / `openConsentDialog` to a component. Both delegate to the
 * component's own injector, so the returned subscriptions are torn down when the component is
 * destroyed (via `takeUntilDestroyed` inside `openSimpleDialog`).
 *
 * @example
 * export class UsersComponent extends withDialogComponent(BaseComponent) {
 *     edit(user: User): void {
 *         this.openDialog<UserFormValue, User>({
 *             header: 'Edit user',
 *             content: UserFormComponent,
 *             context: user,
 *         }).subscribe((value) => { if (value) { ... } });
 *     }
 * }
 *
 * @param Base - The base class to extend (usually `BaseComponent`).
 * @returns A class extending `Base` with the dialog-opening methods.
 */
export function withDialogComponent<TBase extends AbstractConstructor<BaseComponent>>(
  Base: TBase,
): TBase & AbstractConstructor<DialogCapableComponent> {
  abstract class DialogComponentMixin extends Base implements DialogCapableComponent {
    // -----------------------------------------------------------------------------------------------------
    // @ DEPENDENCIES
    // -----------------------------------------------------------------------------------------------------

    private readonly dialogInjector = inject(Injector);

    // -----------------------------------------------------------------------------------------------------
    // @ PUBLIC METHODS
    // -----------------------------------------------------------------------------------------------------

    override openDialog<TReturn = unknown, TContext = unknown>(
      data: Partial<SimpleDialogData<TReturn, TContext>>,
      config?: Omit<MatDialogConfig<SimpleDialogData<TReturn, TContext>>, 'data'>,
    ): Observable<TReturn | undefined> {
      return openSimpleDialog<TReturn, TContext>(this.dialogInjector, data, config);
    }

    openConsentDialog<TContext = unknown>(
      data: Partial<SimpleDialogData<boolean, TContext>>,
      config?: Omit<MatDialogConfig<SimpleDialogData<boolean, TContext>>, 'data'>,
    ): Observable<boolean> {
      return openConsentDialog<TContext>(this.dialogInjector, data, config);
    }
  }

  return DialogComponentMixin;
}

/** The object returned by {@link withDialog}. */
export interface DialogOpener {
  /** See {@link DialogCapableComponent.openDialog}. */
  open<TReturn = unknown, TContext = unknown>(
    data: Partial<SimpleDialogData<TReturn, TContext>>,
    config?: Omit<MatDialogConfig<SimpleDialogData<TReturn, TContext>>, 'data'>,
  ): Observable<TReturn | undefined>;

  /** See {@link DialogCapableComponent.openConsentDialog}. */
  openConsent<TContext = unknown>(
    data: Partial<SimpleDialogData<boolean, TContext>>,
    config?: Omit<MatDialogConfig<SimpleDialogData<boolean, TContext>>, 'data'>,
  ): Observable<boolean>;
}

/**
 * Composable alternative to {@link withDialogComponent} for components that can't use the mixin —
 * e.g. they already extend a generic base such as `BaseFormComponent<...>`, whose type parameters a
 * mixin would erase. Call it from an injection context (a field initializer) and use the returned
 * `open` / `openConsent`.
 *
 * @example
 * private readonly dialog = withDialog();
 * // this.dialog.open<UserFormValue, User>({ ... }).subscribe(...)
 *
 * @returns An object exposing `open` and `openConsent`, bound to the current injector.
 */
export function withDialog(): DialogOpener {
  const injector = inject(Injector);

  return {
    open: <TReturn = unknown, TContext = unknown>(
      data: Partial<SimpleDialogData<TReturn, TContext>>,
      config?: Omit<MatDialogConfig<SimpleDialogData<TReturn, TContext>>, 'data'>,
    ): Observable<TReturn | undefined> =>
      openSimpleDialog<TReturn, TContext>(injector, data, config),

    openConsent: <TContext = unknown>(
      data: Partial<SimpleDialogData<boolean, TContext>>,
      config?: Omit<MatDialogConfig<SimpleDialogData<boolean, TContext>>, 'data'>,
    ): Observable<boolean> => openConsentDialog<TContext>(injector, data, config),
  };
}
